// Content shares the existing authenticated settings endpoint. Namespaced keys
// leave every legacy price and availability key intact; no database migration.
export const ITEM_PREFIX = 'content.item.';
export const PROMOTION_PREFIX = 'content.promotion.';
export const FEATURED_KEY = 'content.featuredItems';
export const BRAND_ORDER_KEY = 'content.drinkBrandOrder';

export const ITEM_CATEGORIES = {
  specialtySandwiches: 'Specialty sandwiches',
  deliMeats: 'Deli meats',
  deliCheeses: 'Deli cheeses',
  deliBreads: 'Deli breads',
  deliCondiments: 'Deli condiments',
  deliSmokedFish: 'Deli smoked fish',
  deliHomemadeSalads: 'Deli homemade salads',
  deliSaladPlates: 'Deli salad plates',
  deliSoups: 'Deli soups',
  deliSpecialties: 'Deli favorites & sides',
  deliTreats: 'Deli treats',
  deliFridgeItems: 'Deli fridge items',
};

export const safeImageUrl = (value) => {
  if (typeof value !== 'string') return '';
  const url = value.trim();
  if (/^\/(?![/\\])[^\s\\]*$/.test(url)) return url;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && !parsed.username && !parsed.password ? parsed.href : '';
  } catch { return ''; }
};

export const customItems = (overrides = {}) => Object.entries(overrides)
  .filter(([key, item]) => key.startsWith(ITEM_PREFIX) && item &&
    Object.prototype.hasOwnProperty.call(ITEM_CATEGORIES, item.moduleKey) && Number.isSafeInteger(item.id) &&
    typeof item.name === 'string' && item.name.trim())
  .map(([key, item]) => ({ ...item, contentKey: key, image: safeImageUrl(item.image),
    price_override_key: `admin-${item.id}`, adminAdded: true }));

// Append only. Sorting/removing catalog rows before applying indexed price
// overrides would silently assign existing prices to different items.
export const appendCustomItems = (moduleKey, data, overrides = {}) => {
  if (!Array.isArray(data)) return data;
  const ids = new Set(data.map(item => item?.id));
  return [...data, ...customItems(overrides).filter(item => item.moduleKey === moduleKey && !ids.has(item.id))];
};

export const orderByIds = (items, ids) => {
  if (!Array.isArray(ids)) return items;
  const ordered = [...new Set(ids)].map(id => items.find(item => String(item.id) === String(id))).filter(Boolean);
  return [...ordered, ...items.filter(item => !ordered.includes(item))];
};

// Place a newly introduced brand beside its default neighbor in older saved
// orders. Once explicitly ordered by an admin, its saved position wins.
export const orderDrinkBrands = (brands, ids) => {
  const ordered = [...orderByIds(brands, ids)];
  if (!Array.isArray(ids)) return ordered;
  brands.filter(brand => brand.defaultAfter && !ids.includes(brand.id)).forEach(brand => {
    const neighbor = ordered.find(item => item.id === brand.defaultAfter);
    if (!neighbor) return;
    ordered.splice(ordered.indexOf(brand), 1);
    ordered.splice(ordered.indexOf(neighbor) + 1, 0, brand);
  });
  return ordered;
};

export const defaultFeaturedIds = (sandwiches) => {
  const newItems = sandwiches.filter(item => item.newAddition || item.badge === 'New Addition');
  const featured = sandwiches.filter(item => item.featured);
  return (newItems.length ? newItems : featured.length ? featured : sandwiches.slice(0, 1)).map(item => `specialtySandwiches.${item.id}`);
};

export const featuredItems = (sandwiches, overrides = {}) => {
  const ids = Array.isArray(overrides[FEATURED_KEY]) ? overrides[FEATURED_KEY] : defaultFeaturedIds(sandwiches);
  return [...new Set(ids)].map(id => sandwiches.find(item => `specialtySandwiches.${item.id}` === id)).filter(Boolean);
};

export const menuPriceError = (value, optional = false) => {
  const text = String(value ?? '').trim().replace(/^\$/, '').trim();
  if (!text) return optional ? '' : 'Enter a price. Use 0 for a free option.';
  if (!/^\d+(\.\d{1,2})?$/.test(text) || !Number.isFinite(Number(text))) {
    return 'Use a price of 0 or more, with up to two decimal places.';
  }
  return '';
};

export const formatAdminPrice = (value) => {
  if (value === '' || value === undefined || value === null) return 'Not listed';
  const number = Number(String(value).replace(/^\$/, '').trim());
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : String(value);
};

export const sameSetting = (left, right) => {
  if (left === right) return true;
  if (left && right && typeof left === 'object' && typeof right === 'object') {
    if (Array.isArray(left) !== Array.isArray(right)) return false;
    const leftKeys = Object.keys(left).filter(key => left[key] !== undefined).sort();
    const rightKeys = Object.keys(right).filter(key => right[key] !== undefined).sort();
    return leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index] && sameSetting(left[key], right[key]));
  }
  if (typeof left === 'object' || typeof right === 'object') return false;
  return left !== undefined && right !== undefined && String(left) === String(right);
};

export const mergeSettings = (saved, edits) => {
  const next = { ...saved };
  Object.entries(edits).forEach(([key, value]) => {
    if (value === undefined) delete next[key];
    else next[key] = value;
  });
  return next;
};

export const detroitDate = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Detroit', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

const isDate = value => !value || (/^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value);

export const promotionError = (promotion) => {
  if (!promotion.title?.trim()) return 'Give this promotion a title.';
  if (!promotion.message?.trim()) return 'Add the offer or announcement customers should see.';
  if (!isDate(promotion.start) || !isDate(promotion.end)) return 'Choose valid start and end dates.';
  if (promotion.start && promotion.end && promotion.end < promotion.start) return 'The end date must be on or after the start date.';
  return '';
};

export const promotionStatus = (promotion, today = detroitDate()) => {
  if (!promotion.enabled) return 'Paused';
  if (promotionError(promotion)) return 'Needs attention';
  if (promotion.start && promotion.start > today) return 'Scheduled';
  if (promotion.end && promotion.end < today) return 'Ended';
  return 'Active';
};

export const getPromotions = (overrides = {}) => Object.entries(overrides)
  .filter(([key, value]) => key.startsWith(PROMOTION_PREFIX) && value && typeof value === 'object')
  .map(([key, value]) => ({ ...value, contentKey: key }));

export const PROMOTION_LINKS = { '/menu': 'Sandwich menu', '/deli': 'Deli menu', '/drinks': 'Drinks', '/snacks': 'Snacks', '/catering': 'Catering', 'tel:248-352-7377': 'Call to order' };

export const activePromotions = (overrides, pathname, today = detroitDate()) => getPromotions(overrides)
  .filter(promotion => promotionStatus(promotion, today) === 'Active' &&
    (promotion.placement === 'all' || (promotion.placement === 'home' && pathname === '/') ||
      (promotion.placement === 'menus' && ['/menu', '/deli', '/drinks', '/snacks', '/catering'].includes(pathname))))
  .sort((left, right) => Number(Boolean(right.pinned)) - Number(Boolean(left.pinned)));
